/**
 * useAuth Composable
 *
 * Vue 3 composition API composable for SuperTokens authentication.
 * Provides reactive auth state and methods for sign up, sign in, sign out, etc.
 */

import Session from "supertokens-web-js/recipe/session";
import EmailPassword from "supertokens-web-js/recipe/emailpassword";
import EmailVerification from "supertokens-web-js/recipe/emailverification";
import ThirdParty from "supertokens-web-js/recipe/thirdparty";

/**
 * Global auth state (shared across all instances)
 */
const isAuthenticated = ref(false);
const isAuthReady = ref(false); // True after initial session check completes
const userId = ref<string | null>(null);
const isLoading = ref(false);
const error = ref<string | null>(null);

/**
 * Authentication composable
 */
export function useAuth() {
  /**
   * Sign up with email and password
   */
  const signUp = async (email: string, password: string) => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await EmailPassword.signUp({
        formFields: [
          { id: "email", value: email },
          { id: "password", value: password },
        ],
      });

      if (response.status === "OK") {
        isAuthenticated.value = true;
        userId.value = response.user.id;
      }

      return response;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Sign in with email and password
   */
  const signIn = async (email: string, password: string) => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await EmailPassword.signIn({
        formFields: [
          { id: "email", value: email },
          { id: "password", value: password },
        ],
      });

      if (response.status === "OK") {
        isAuthenticated.value = true;
        userId.value = response.user.id;
      }

      return response;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Sign out current user
   */
  const signOut = async (): Promise<void> => {
    isLoading.value = true;
    error.value = null;

    try {
      await Session.signOut();

      // Clear auth state
      isAuthenticated.value = false;
      userId.value = null;
    } finally {
      isLoading.value = false;
    }
  };

  /**
   * Check if user has an active session
   */
  const checkSession = async (): Promise<boolean> => {
    try {
      const hasSession = await Session.doesSessionExist();

      if (hasSession) {
        const sessionUserId = await Session.getUserId();
        isAuthenticated.value = true;
        userId.value = sessionUserId;
      } else {
        isAuthenticated.value = false;
        userId.value = null;
      }

      return hasSession;
    } catch (err) {
      console.error("Session check failed:", err);
      isAuthenticated.value = false;
      userId.value = null;
      return false;
    } finally {
      // Always mark auth as ready after initial check completes
      isAuthReady.value = true;
    }
  };

  /**
   * Send password reset email
   */
  const sendPasswordResetEmail = async (email: string) => {
    return EmailPassword.sendPasswordResetEmail({
      formFields: [{ id: "email", value: email }],
    });
  };

  /**
   * Reset password with token
   */
  const resetPassword = async (newPassword: string) => {
    return EmailPassword.submitNewPassword({
      formFields: [{ id: "password", value: newPassword }],
    });
  };

  /**
   * Verify email (consume token from URL)
   */
  const verifyEmail = async () => {
    return EmailVerification.verifyEmail();
  };

  /**
   * Check if user's email is verified
   */
  const isEmailVerified = async (): Promise<boolean> => {
    const response = await EmailVerification.isEmailVerified();
    return response.isVerified;
  };

  /**
   * Send verification email to current user
   */
  const sendVerificationEmail = async () => {
    return EmailVerification.sendVerificationEmail();
  };

  /**
   * Redirect to OAuth provider's consent screen
   */
  const signInWithProvider = async (thirdPartyId: 'google' | 'facebook') => {
    const response = await ThirdParty.getAuthorisationURLWithQueryParamsAndSetState({
      thirdPartyId,
      frontendRedirectURI: `${window.location.origin}/auth/callback`,
    });
    window.location.assign(response);
  };

  /**
   * Handle OAuth callback - exchange auth code for session
   */
  const handleOAuthCallback = async () => {
    const response = await ThirdParty.signInAndUp();

    if (response.status === 'OK') {
      isAuthenticated.value = true;
      userId.value = response.user.id;
    }

    return response;
  };

  // Note: Session check is handled by the SuperTokens plugin
  // Do NOT call checkSession here - it will run before SuperTokens is initialized

  return {
    // State
    isLoading: readonly(isLoading),
    isAuthenticated: readonly(isAuthenticated),
    isAuthReady: readonly(isAuthReady),
    userId: readonly(userId),
    error: readonly(error),

    // Methods
    signUp,
    signIn,
    signOut,
    checkSession,
    sendPasswordResetEmail,
    resetPassword,
    verifyEmail,
    isEmailVerified,
    sendVerificationEmail,
    signInWithProvider,
    handleOAuthCallback,
  };
}
